// =============================================================================
// domain/excursionExit.ts — the EXIT SKILL measures.
// -----------------------------------------------------------------------------
// Entry Edge asks whether the setup offers anything. These ask whether you keep
// it: what you realised against what was actually there, the dollars left on the
// table, and how far it ran after you walked away.
//
//   Hold-duration edge   where in the hold your edge lives
//   Cost of give-back    dollars between the in-trade peak and what you banked
//   Cost of premature exit   dollars it kept running AFTER you left
//
// ── 🚨 GIVE-BACK AND PREMATURE EXIT ARE OPPOSITE HABITS, NOT ONE NUMBER ──
//
// Give-back is exiting too LATE: the trade reached a peak while you held it and
// you handed some back. Premature exit is leaving too EARLY: the move continued
// once you were flat. A trade can do both, and a surface that added them would
// report a trader who is simultaneously too patient and too hasty. They are
// measured separately and the card compares them so the dominant habit is
// visible.
//
// ⚠ Give-back reads MFE, which is bounded by the trade's own life. Premature
// exit reads the STORED post-exit walk, which exists only for trades that were
// put through the old client's metered recompute — so their populations differ
// and each states its own.
//
// ── ⚠ POINTS WHERE POSSIBLE, DOLLARS ONLY WHERE THE QUESTION IS DOLLARS ──
//
// The hold-duration card reports give-back in POINTS, which is exact per
// contract and needs no size assumption. The cost cards report DOLLARS, because
// "what did this habit cost me" is a dollar question — and that is the only
// reason they need the contract catalogue.
// =============================================================================

import { median } from './excursionStats';
import { peakExposure, type Execution as PeakExecution } from './capture';
import { prematureExitValue, type ExtensionTrade } from './leakExtension';
import type { PnlUnit } from './types';
import {
  tradeValue, excursionValue, dollarsPerPointFor, type ContractSpec } from './trade';
import { groupKeyOf, type GroupBy, type NameLookup } from './excursionEdge';

/** What the exit-skill measures need. Extends the extension primitive's shape so
 *  one row type serves all three cards. */
export interface ExitTrade extends ExtensionTrade {
  patternId: string | null;
  exitModelId: string | null;
  /**
   * ⚠ REQUIRED HERE, though `ExtensionTrade` has it optional. The hold table's
   * average follows the display unit now, and in ticks that needs the
   * contract's tick size — an absent contract is a silent null column.
   */
  contract: string | null;
  timeIn: string | null;
  timeOut: string | null;
  mfePoints: number | null;
  mfeSecs: number | null;
  /**
   * The realised R the trader recorded.
   *
   * ⚠ DECLARED BECAUSE THE R PATH DEPENDS ON IT. `tradeValue` reads this for the
   * realised leg of give-back, and it is deliberately NOT re-derived from points
   * ÷ risk: R is what the trader planned against, and a trade that never carried
   * one has no R for give-back to measure. tsc caught this — the tests passed
   * without it because vitest does not typecheck.
   */
  rrRealized: number | null;
  points: number | null;
  netPnl: number | null;
  /**
   * ⚠ THE FEE-FREE LEG, and give-back is measured against it. Peak-to-exit is a
   * price move with no commission in it, so netting one side and not the other
   * folds every fee into the give-back figure.
   */
  grossPnl: number | null;
  /** Only used to reconstruct gross on rows that predate that column. */
  fees: number | null;
  /**
   * The fill timeline, for the give-back reconstruction.
   *
   * ⚠ REQUIRED, NOT OPTIONAL, and that is deliberate. The Excursion tab builds
   * these objects behind an `as unknown as ExitTrade` cast, which is exactly how
   * `rrRealized` went missing and stayed missing (#67) — an optional field would
   * simply never be supplied and nothing would say so. Null is a real answer
   * ("this trade has no fills recorded"); absent is a bug.
   */
  executions: readonly PeakExecution[] | null;
}

/** ⚠ `groupKeyOf` takes the Entry Edge shape, which requires both excursions;
 *  these cards need only the two ids. Narrowed here rather than widening
 *  `ExitTrade` with fields no exit-skill measure reads. */
const groupIdOf = (t: ExitTrade, by: GroupBy): string | null =>
  groupKeyOf(
    { patternId: t.patternId, exitModelId: t.exitModelId } as Parameters<typeof groupKeyOf>[0],
    by,
  );

// ---------------------------------------------------------------------------
// Hold duration
// ---------------------------------------------------------------------------

export interface HoldBucket { lo: number; hi: number; label: string }

export const HOLD_BUCKETS: readonly HoldBucket[] = [
  { lo: 0, hi: 1, label: '0–1m' },
  { lo: 1, hi: 3, label: '1–3m' },
  { lo: 3, hi: 5, label: '3–5m' },
  { lo: 5, hi: 10, label: '5–10m' },
  { lo: 10, hi: 20, label: '10–20m' },
  { lo: 20, hi: Infinity, label: '20m+' },
];

/**
 * Minutes held, from the two clock strings.
 *
 * ⚠ WRAPS PAST MIDNIGHT. A trade opened at 23:50 and closed at 00:05 is fifteen
 * minutes, not minus 1,425 — and futures sessions run through midnight, so this
 * is an ordinary case rather than an edge one.
 */
export function holdMinutes(t: Pick<ExitTrade, 'timeIn' | 'timeOut'>): number | null {
  const a = t.timeIn; const b = t.timeOut;
  if (!a || !b) return null;
  const pa = a.split(':'); const pb = b.split(':');
  if (pa.length < 2 || pb.length < 2) return null;
  const mi = Number(pa[0]) * 60 + Number(pa[1]);
  const mo = Number(pb[0]) * 60 + Number(pb[1]);
  if (!Number.isFinite(mi) || !Number.isFinite(mo)) return null;
  const d = mo - mi;
  return d < 0 ? d + 1440 : d;
}

export interface HoldRow {
  label: string;
  /** Trades in this bucket carrying a favourable excursion and a hold. */
  n: number;
  /**
   * The bucket's average P&L IN THE DISPLAY UNIT (#67) — named for it, because
   * it averaged raw dollars under a "pts" header for a month.
   *
   * ⚠ THE DENOMINATOR IS THE VALUED TRADES, not the bucket. In R a stop-less
   * trade has no value; counting it in the divisor would halve the average of a
   * bucket that is half stop-less. Beta divides by its own `gv`.
   */
  avgUnitPnl: number | null;
  /** Points between the peak and what was banked. Per contract, so exact. */
  avgGiveBackPoints: number | null;
  /** Median share of the offered move kept, WINNERS only, 0–100. */
  winnerCapturePct: number | null;
  /** Share of the ≥1pt set that still closed red — winners handed back. */
  roundTripPct: number | null;
  winRatePct: number | null;
}

export interface HoldDurationEdge {
  rows: HoldRow[];
  /** Trades that qualified at all — a positive MFE and a readable hold. */
  sample: number;
  medianHoldMinutes: number | null;
  avgGiveBackPoints: number | null;
  /** Median seconds to the favourable peak, over trades carrying the timing. */
  medianPeakSecs: number | null;
  /** How many carried it — the tile is meaningless without the count. */
  peakSample: number;
  best: HoldRow | null;
  worst: HoldRow | null;
}

/**
 * Where in the hold the edge lives.
 *
 * ⚠ THE ≥1 POINT GATE APPLIES TO CAPTURE AND ROUND-TRIPS ONLY, not to the
 * bucket's P&L. A trade that never offered a point has no meaningful capture
 * ratio — `0.2 / 0.1` is 200% — but its P&L is still real and belongs in the
 * bucket average.
 *
 * ⚠ WINNER CAPTURE IS WINNERS ONLY, so it stays inside 0–100 and answers "of
 * what was offered, how much did I keep". The trades that ran green and closed
 * red are counted separately as ROUND-TRIPS rather than folded in as negative
 * capture — which is the same distinction the dashboard's median makes, and the
 * reason a mean far below the median means give-back losers.
 */
export function holdDurationEdge(
  trades: readonly ExitTrade[],
  catalogue: readonly ContractSpec[],
  unit: PnlUnit,
): HoldDurationEdge {
  interface Row {
    hold: number; mfe: number; pts: number; pnl: number; gb: number; secs: number | null;
    /** The trade in the display unit, or null when it has none in that unit. */
    uv: number | null;
  }
  const rows: Row[] = [];

  for (const t of trades) {
    const mfe = Number(t.mfePoints);
    if (!Number.isFinite(mfe) || mfe <= 0) continue;
    const hold = holdMinutes(t);
    if (hold == null) continue;
    const pts = Number(t.points) || 0;
    rows.push({
      hold,
      mfe,
      pts,
      // ⚠ `pnl` IS STILL DOLLARS AND STILL USED — the win rate reads its sign.
      // Only the average follows the unit.
      pnl: Number(t.netPnl) || 0,
      uv: tradeValue(t, unit, catalogue),
      // ⚠ Floored at zero. A trade that closed BEYOND its own in-trade peak gave
      // nothing back; a negative give-back would net against real give-back
      // elsewhere and understate the habit.
      gb: Math.max(0, mfe - pts),
      // ⚠ `t.mfeSecs == null` FIRST. `Number(null)` is **0**, not NaN, so
      // `Number.isFinite(Number(null))` is TRUE — a trade with no logger timing
      // was being recorded as peaking at zero seconds, dragging the median
      // time-to-peak toward instant and making every setup look like a rip.
      // Caught by a test asserting the tile's own sample count.
      secs: t.mfeSecs == null || !Number.isFinite(Number(t.mfeSecs))
        ? null : Number(t.mfeSecs),
    });
  }

  const bucketRows: HoldRow[] = HOLD_BUCKETS.map((b) => {
    const g = rows.filter((r) => r.hold >= b.lo && r.hold < b.hi);
    // ⚠ The average's own population — see `avgUnitPnl`.
    const gv = g.filter((r) => r.uv != null);
    const offered = g.filter((r) => r.mfe >= 1);
    const winnerCaps = offered.filter((r) => r.pts > 0).map((r) => (r.pts / r.mfe) * 100);
    const roundTrips = offered.filter((r) => r.pts <= 0).length;
    return {
      label: b.label,
      n: g.length,
      avgUnitPnl: gv.length ? gv.reduce((s, r) => s + (r.uv as number), 0) / gv.length : null,
      avgGiveBackPoints: g.length ? g.reduce((s, r) => s + r.gb, 0) / g.length : null,
      winnerCapturePct: median(winnerCaps),
      roundTripPct: offered.length ? (100 * roundTrips) / offered.length : null,
      winRatePct: g.length ? (100 * g.filter((r) => r.pnl > 0).length) / g.length : null,
    };
  });

  const filled = bucketRows.filter((r) => r.n > 0);
  const withSecs = rows.filter((r) => r.secs != null).map((r) => r.secs as number);

  return {
    rows: bucketRows,
    sample: rows.length,
    medianHoldMinutes: median(rows.map((r) => r.hold)),
    avgGiveBackPoints: rows.length ? rows.reduce((s, r) => s + r.gb, 0) / rows.length : null,
    medianPeakSecs: median(withSecs),
    peakSample: withSecs.length,
    best: filled.length
      ? filled.reduce((a, b) => ((b.avgUnitPnl ?? -Infinity) > (a.avgUnitPnl ?? -Infinity) ? b : a))
      : null,
    worst: filled.length
      ? filled.reduce((a, b) => ((b.avgUnitPnl ?? Infinity) < (a.avgUnitPnl ?? Infinity) ? b : a))
      : null,
  };
}

// ---------------------------------------------------------------------------
// Cost of give-back
// ---------------------------------------------------------------------------

/** Below this there was no favourable excursion worth calling a give-back. */
export const GIVEBACK_MIN_POINTS = 0.5;

export interface GiveBackCost {
  /** Trades with a meaningful peak. */
  sample: number;
  /** Dollars, always ≥ 0. */
  total: number;
  /** Reached a peak, gave some back, still closed green. */
  winnerCost: number;
  winnerCount: number;
  /** Reached a favourable peak, then round-tripped into a loss. */
  loserCost: number;
  loserCount: number;
  /** Trades dropped for want of a dollars-per-point — stated, never silent. */
  unpriced: number;
  /**
   * How many trades used the NAIVE figure because their fills could not be
   * reconstructed.
   *
   * ⚠ Above zero, the total is an upper bound and the screen must say so. Beta
   * marks the same figure `≈`. A bound presented as a measurement is the defect
   * this field exists to make visible — not a detail for the changelog.
   */
  approx: number;
}

/**
 * Dollars between each trade's best in-trade price and what it banked.
 *
 * ── ⚠ THIS IS GIVE-BACK FROM THE PEAK, NOT A PREMATURE EXIT ──
 *
 * A trade that keeps running AFTER the exit is not here — that is the other card.
 * The old client's copy is emphatic about it and the confusion is easy: both
 * describe "money you did not get".
 *
 * ⚠ MEASURED AGAINST GROSS, not net. The peak-to-exit distance is a price move
 * and carries no fees, so netting one side and not the other would fold every
 * commission into the give-back and inflate it by the cost of trading.
 *
 * ── 🚨 THE POSITION IS RECONSTRUCTED, NOT ASSUMED (#88) ──
 *
 * This used to be `mfePoints × $/point × finalQty − realised`, and the comment
 * here said so out loud: "an upper bound … a scaled-out trade gave back less
 * than this says." It was an upper bound reported as a measurement, on a
 * BEHAVIOURAL number — the app telling a trader how much they let run away from
 * them — which then feeds the leak ranking the whole method is built on.
 *
 * 🔴 AND THE CORRECT VERSION ALREADY EXISTED, TESTED, WITH ONE CALLER. The Day
 * Report walked the fills through `peakExposure` and got the right answer while
 * this card got a different one for the same trade. Two screens in one app
 * disagreeing about one number, and only one of them right.
 *
 * So the FIFO walk is tried first here too. When it cannot run — no fills, no
 * MFE timestamp, a timeline that does not reconcile — the naive figure is used
 * and `approx` counts it, so the screen can mark the total `≈` rather than
 * present a bound as a measurement. That is what the old client does.
 *
 * ⚠ The count of what could not be PRICED at all is separate and still returned.
 */
/**
 * A points quantity in `unit`, KEEPING ITS SIGN.
 *
 * ⚠ `excursionValue` takes a MAGNITUDE — it calls `Math.abs` — because an
 * excursion has no direction. The two costs here do: premature exit is negative
 * when holding would have lost money, and printing that as a gain would invert
 * the advice. So the magnitude is converted by the tested function and the sign
 * is put back, rather than writing a second conversion that could drift.
 *
 * Null when the trade has no value in the unit — an uncatalogued contract has no
 * tick size, a stop-less trade no R.
 */
function signedIn(
  t: ExitTrade, unit: PnlUnit, points: number, catalogue: readonly ContractSpec[],
): number | null {
  const mag = excursionValue(t as never, unit, Math.abs(points), catalogue);
  if (mag == null) return null;
  return points < 0 ? -mag : mag;
}

export function giveBackCost(
  trades: readonly ExitTrade[], catalogue: readonly ContractSpec[] = [],
  unit: PnlUnit = 'usd',
): GiveBackCost {
  let total = 0; let winnerCost = 0; let loserCost = 0;
  let sample = 0; let winnerCount = 0; let loserCount = 0; let unpriced = 0;
  let approx = 0;

  for (const t of trades) {
    const mfe = Number(t.mfePoints);
    if (!Number.isFinite(mfe) || mfe < GIVEBACK_MIN_POINTS) continue;

    // ── 🚨 THE NON-DOLLAR PATH, WHICH IS NOT THE DOLLAR ONE SCALED ──
    //
    // In dollars the peak is `mfe × $/point × qty` and the realised leg is the
    // GROSS P&L. In points, ticks and R both sides are per-contract price moves
    // and there is no quantity and no fee in either — so multiplying the dollar
    // answer by anything would be wrong twice over.
    //
    // This is the branch beta takes (`giveBackForTrade`), and the per-trade
    // version in `domain/dayReport` already takes it. Same shape here.
    //
    // ⚠ The exact peak-exposure reconstruction below is DELIBERATELY not used
    // outside dollars: it corrects a quantity assumption, and there is no
    // quantity in a per-contract figure to correct.
    if (unit !== 'usd') {
      const peakV = signedIn(t, unit, mfe, catalogue);
      const realV = tradeValue(t as never, unit, catalogue);
      if (peakV == null || realV == null) { unpriced += 1; continue; }
      const cost = Math.max(0, peakV - realV);
      // 🔴 A TRADE THAT GAVE NOTHING BACK IS NOT ONE THAT DID. See the note on
      // the dollar branch below — same rule, same reason.
      if (cost <= 0) continue;
      sample += 1;
      total += cost;
      if ((Number(t.netPnl) || 0) > 0) { winnerCost += cost; winnerCount += 1; }
      else { loserCost += cost; loserCount += 1; }
      continue;
    }

    const dpp = dollarsPerPointFor(t.contract ?? null, catalogue);
    const qty = Number(t.qty);
    if (!dpp || !Number.isFinite(qty) || qty <= 0) { unpriced += 1; continue; }

    // ⚠ Gross where it exists, else net + fees — the same fallback the old
    // client uses for rows that predate the gross column.
    //
    // ⚠ `== null` FIRST, and the fallback did not fire without it. `Number(null)`
    // is **0**, so `Number.isFinite(Number(null))` is TRUE: a row with no gross
    // read as having banked exactly nothing, and the whole peak was reported as
    // given back. On a $60 peak that is a $60 cost where the real one was $10.
    const realized = t.grossPnl != null && Number.isFinite(Number(t.grossPnl))
      ? Number(t.grossPnl)
      : (Number(t.netPnl) || 0) + (Number(t.fees) || 0);

    // 🚨 THE FIFO WALK FIRST. `peakExposure` replays the fills and returns what
    // was ACTUALLY on at the moment the high printed; the multiplication below
    // assumes the whole final position was. Same call, same shape, same fallback
    // order as `domain/dayReport.ts` — the two screens now compute one number.
    const exact = peakExposure(
      {
        entry: t.entry, side: t.side, timeIn: t.timeIn,
        mfePoints: t.mfePoints, mfeSecs: t.mfeSecs,
        executions: t.executions ?? null,
      } as never,
      dpp,
    );

    let cost: number;
    if (exact) {
      cost = exact.giveBackUsd;
    } else {
      // ⚠ The old shape, kept for trades whose fills cannot be reconstructed —
      // and COUNTED, so the screen can mark the total as approximate.
      const peak = mfe * dpp * qty;
      cost = Math.max(0, peak - realized);
      approx += 1;
    }

    // 🔴 A TRADE THAT GAVE NOTHING BACK IS NOT COUNTED AS ONE THAT DID, and
    // this line is the whole fix. Beta skips it outright — `if (gb.value > 0)`,
    // index.html:38803 — and this client used to add it: $0 to the total, which
    // is invisible, and +1 to `sample`, which is printed on the tile.
    //
    // ⚠ THE DOLLARS WERE NEVER WRONG, which is why it survived. `giveBackUsd`
    // is floored per lot (`capture.ts:263`) and the fallback is `Math.max(0,…)`,
    // so a zero-cost trade adds exactly nothing to the money. Only the count
    // moved — the same leak spread over more trades, which reads as a smaller
    // average leak per trade than beta reports on the same book.
    if (cost <= 0) continue;

    sample += 1;
    total += cost;
    // ⚠ Split on the REALISED outcome, not on whether there was a cost. A
    // round-tripped winner is the expensive habit and it has to be separable.
    if ((Number(t.netPnl) || 0) > 0) { winnerCost += cost; winnerCount += 1; }
    else { loserCost += cost; loserCount += 1; }
  }

  return {
    sample, total, winnerCost, winnerCount, loserCost, loserCount, unpriced, approx,
  };
}

// ---------------------------------------------------------------------------
// Cost of premature exit
// ---------------------------------------------------------------------------

export const PREMATURE_MIN_SAMPLE = 5;

export interface PrematureRow {
  id: string;
  name: string;
  sample: number;
  /** Signed dollars. Negative means holding would have COST money. */
  net: number;
  avg: number;
  /** Share that kept running after the exit — money genuinely left behind. */
  keptPct: number;
  /** Share that would have hit the original stop — the exit was protective. */
  protectivePct: number;
  /** Median qualified run in points, over the non-stopped trades only. */
  medianPoints: number | null;
}

export interface PrematureExitCost {
  /**
   * ⚠ THE HEADLINE IS TAG-INDEPENDENT. Every priceable trade counts here, even
   * one with no pattern — otherwise the total understates itself by exactly the
   * trades nobody got round to tagging, which is the population most likely to
   * be sloppy in the first place.
   */
  net: number;
  sample: number;
  /** How many of those were protective exits. */
  stopped: number;
  rows: PrematureRow[];
}

/**
 * What holding instead of exiting would have been worth.
 *
 * ⚠ THE PRIMITIVE IS SHARED WITH THE COACH'S REPORT'S LEAK, deliberately. Two
 * implementations of "was that exit early" would eventually disagree, and the
 * trader would be told one thing on one tab and another somewhere else.
 *
 * ⚠ A HEAD-FAKE IS A COST, NEVER A CREDIT. A trade that ran, then reversed into
 * where the stop was, would have lost money if held — `prematureExitValue`
 * returns that as a negative and it nets against the money genuinely left
 * behind. Counting only the favourable runs is how a surface tells someone every
 * exit was early.
 */
export function prematureExitCost(
  trades: readonly ExitTrade[],
  catalogue: readonly ContractSpec[],
  by: GroupBy,
  nameOf: NameLookup,
  unit: PnlUnit = 'usd',
): PrematureExitCost {
  let net = 0; let sample = 0; let stopped = 0;
  const groups = new Map<string, {
    name: string; net: number; n: number; kept: number; stopped: number; pts: number[];
  }>();

  for (const t of trades) {
    const pv = prematureExitValue(t, catalogue);
    if (!pv) continue;
    // ── ⚠ THE SAME QUANTITY, IN THE DISPLAY UNIT ──
    //
    // `pointsValue` and NOT `points`: in the stopped branch `points` is the
    // qualified run while the VALUE is built from the hold-to-stop distance, so
    // converting via `points` prices a different leg and yields a figure that
    // looks entirely reasonable.
    //
    // ⚠ A trade with no value in the unit is SKIPPED, not zeroed — a zero here
    // would read as "holding would have changed nothing", which is a real and
    // different finding.
    const value = unit === 'usd' ? pv.value : signedIn(t, unit, pv.pointsValue, catalogue);
    if (value == null) continue;

    net += value;
    sample += 1;
    if (pv.stopped) stopped += 1;

    const id = groupIdOf(t, by);
    if (!id) continue;
    const name = nameOf(id);
    if (name == null) continue;

    const g = groups.get(id) ?? { name: name || '(Untitled)', net: 0, n: 0, kept: 0, stopped: 0, pts: [] };
    g.net += value;
    g.n += 1;
    if (pv.stopped) g.stopped += 1;
    else {
      if (value > 0) g.kept += 1;
      // ⚠ Only exits that never touched the stop produced a run that was
      // actually reachable. A head-fake's pre-reversal peak is not opportunity.
      g.pts.push(pv.points);
    }
    groups.set(id, g);
  }

  const rows: PrematureRow[] = [];
  for (const [id, g] of groups) {
    if (g.n < PREMATURE_MIN_SAMPLE) continue;
    rows.push({
      id, name: g.name, sample: g.n,
      net: g.net, avg: g.net / g.n,
      keptPct: (100 * g.kept) / g.n,
      protectivePct: (100 * g.stopped) / g.n,
      medianPoints: median(g.pts),
    });
  }

  return { net, sample, stopped, rows };
}

/**
 * Which habit dominates — leaving early, or holding too long.
 *
 * ⚠ Returns null when either side has no population. "Neither" and "we could
 * not measure one of them" are different answers, and the second must not print
 * as a balanced verdict.
 */
export function dominantExitHabit(
  giveBack: GiveBackCost, premature: PrematureExitCost,
): 'give-back' | 'premature' | 'balanced' | null {
  if (!giveBack.sample || !premature.sample) return null;
  const late = giveBack.total;
  const early = Math.max(0, premature.net);
  if (late === 0 && early === 0) return 'balanced';
  const ratio = late / Math.max(early, 1e-9);
  if (ratio > 1.25) return 'give-back';
  if (ratio < 0.8) return 'premature';
  return 'balanced';
}
